import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Play, X, Star, ArrowRight, BadgeCheck, MapPin, Sparkles, MessageCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import { REVIEW_STATS } from '../data/reviewsData';

/**
 * VIDEOS — single source of truth for testimonial videos.
 * Each item supports either an `mp4Url` (self-hosted asset) or a `youtubeId`.
 * The previous AI-generated YouTube videos have been removed at the user's request.
 */
const TESTIMONIAL_VIDEOS = [
  {
    id: 'termite-1000277019',
    category: 'termite',
    title: 'Termite Treatment — Real Customer Story',
    name: 'EntoRid Customer',
    city: 'Dehradun',
    duration: '1:07',
    // Self-hosted H.264 transcode (original was HEVC — Chrome could play audio but not picture).
    mp4Url: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/testimonials/termite-story-h264.mp4`,
    poster: '',
  },
];

/**
 * CATEGORIES — slideable sub-menu chips (same UX as Gallery).
 * Empty categories render a friendly "Coming soon" placeholder instead of a blank grid.
 */
const CATEGORIES = [
  { key: 'all',       label: 'All Reviews' },
  { key: 'termite',   label: 'Termite' },
  { key: 'cockroach', label: 'Cockroach' },
  { key: 'bedbug',    label: 'Bedbug' },
  { key: 'mosquito',  label: 'Mosquito' },
  { key: 'rodent',    label: 'Rodent' },
  { key: 'general',   label: 'General Pest Control' },
];

export default function TestimonialsVideos({ onOpenCalculator }) {
  useSeo(
    'Video Testimonials',
    'Watch real EntoRid customer video testimonials — termite, cockroach, rodent, mosquito control experiences from Dehradun, Haridwar, Rishikesh and more.',
  );

  const [activeVideo, setActiveVideo] = useState(null);
  const [activeCat, setActiveCat] = useState('all');
  const [cmsVideos, setCmsVideos] = useState([]);

  useEffect(() => {
    let active = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/video-testimonials`).then((res) => {
      if (!active) return;
      setCmsVideos((res.data.items || []).map((video) => ({
        ...video,
        mp4Url: video.video_url ? `${process.env.REACT_APP_BACKEND_URL}${video.video_url}` : '',
        poster: video.cover_url ? `${process.env.REACT_APP_BACKEND_URL}${video.cover_url}` : '',
        name: video.customer_name || 'EntoRid Customer', city: video.location || 'Uttarakhand', duration: video.duration_label || '',
      })));
    }).catch(() => {});
    return () => { active = false; };
  }, []);
  const allVideos = useMemo(() => [...cmsVideos, ...TESTIMONIAL_VIDEOS], [cmsVideos]);

  React.useEffect(() => {
    if (!activeVideo) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setActiveVideo(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeVideo]);

  const filteredVideos = useMemo(
    () => (activeCat === 'all' ? allVideos : allVideos.filter((v) => v.category === activeCat)),
    [activeCat, allVideos],
  );

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 text-sm text-gray-500" data-testid="breadcrumb">
          <Link to="/" className="hover:text-brand-green">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-500">Testimonials</span>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">Video Testimonials</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-blue via-brand-blue-light to-brand-blue-dark text-white">
        <div aria-hidden className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-green/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-1.5 text-xs sm:text-sm font-medium">
            <Sparkles size={14} className="text-brand-green-light" /> Real Customer Stories
          </span>
          <h1 className="mt-5 text-3xl sm:text-5xl font-bold tracking-tight" data-testid="videos-title">
            Video Testimonials
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
            Hear directly from {REVIEW_STATS.totalCustomers} happy customers about their EntoRid experience.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/85">
            <span className="inline-flex items-center gap-1.5"><BadgeCheck size={15} className="text-brand-green-light" /> 100% Verified</span>
            <span className="inline-flex items-center gap-1.5">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}
              </div>
              {REVIEW_STATS.averageRating} rating
            </span>
          </div>
          <Link
            to="/testimonials/reviews"
            data-testid="switch-to-reviews"
            className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white/10 text-white border border-white/25 hover:bg-white/20 font-semibold text-sm transition-colors"
          >
            Read Written Reviews <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Slideable category sub-menu (matches Gallery pattern) */}
      <div className="sticky top-[var(--app-header-h,72px)] z-30 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div
            className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth no-scrollbar"
            data-testid="testimonials-category-rail"
          >
            {CATEGORIES.map((c) => {
              const isActive = activeCat === c.key;
              const count = c.key === 'all'
                ? allVideos.length
                : allVideos.filter((v) => v.category === c.key).length;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setActiveCat(c.key)}
                  data-testid={`testimonial-cat-${c.key}`}
                  className={`whitespace-nowrap inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-semibold border transition-all shrink-0 ${
                    isActive
                      ? 'bg-brand-green border-brand-green text-white shadow-md'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-brand-green/40 hover:text-brand-green'
                  }`}
                >
                  {c.label}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Video grid */}
      <main className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 md:py-16">
        {filteredVideos.length === 0 ? (
          <div className="text-center py-16 sm:py-24" data-testid="testimonials-empty">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-green/10 text-brand-green mb-4">
              <Play size={24} />
            </div>
            <h3 className="text-xl font-semibold text-slate-900">More stories coming soon</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              We&apos;re recording fresh testimonials in this category. Switch to <button onClick={() => setActiveCat('all')} className="text-brand-green font-semibold underline">All Reviews</button> or check back next week.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid="videos-grid">
            {filteredVideos.map((video) => (
              <VideoCard key={video.id} video={video} onPlay={() => setActiveVideo(video)} />
            ))}
          </div>
        )}
      </main>

      {/* Modal player — supports mp4 and YouTube embeds */}
      {activeVideo && (
        <div
          data-testid="video-modal"
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveVideo(null)}
              data-testid="video-close-btn"
              aria-label="Close video"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
            >
              <X size={18} />
            </button>
            <div className="relative aspect-video bg-black">
              {activeVideo.mp4Url ? (
                <video
                  src={activeVideo.mp4Url}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain bg-black"
                  data-testid="video-mp4-player"
                >
                  Your browser does not support video playback.
                </video>
              ) : (
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0`}
                  title={activeVideo.title}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              )}
            </div>
            <div className="p-4 sm:p-5 bg-brand-blue text-white">
              <h3 className="font-semibold text-base sm:text-lg">{activeVideo.title}</h3>
              <p className="text-sm text-white/75 mt-0.5">{activeVideo.name} · {activeVideo.city}</p>
            </div>
          </div>
        </div>
      )}

      {/* Final CTA */}
      <section className="relative overflow-hidden py-14 sm:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-brand-blue-light to-brand-green" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-2xl sm:text-4xl font-bold leading-tight" data-testid="videos-final-cta">
            Ready to Join {REVIEW_STATS.totalCustomers} Happy Customers?
          </h2>
          <p className="mt-3 text-base sm:text-lg text-white/85">
            Book a professional service in minutes. Same-day appointments available.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button onClick={onOpenCalculator} data-testid="videos-book-btn" className="h-12 px-7 rounded-full bg-brand-green hover:bg-brand-green-dark text-white font-semibold shadow-lg">
              Book Now
            </Button>
            <Button onClick={onOpenCalculator} variant="outline" data-testid="videos-inspection-btn" className="h-12 px-7 rounded-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-semibold">
              Get Free Inspection
            </Button>
            <a href="https://api.whatsapp.com/send?phone=919410110910" target="_blank" rel="noreferrer" data-testid="videos-wa-btn" className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-brand-blue hover:bg-white/95 font-semibold shadow-lg">
              <MessageCircle size={18} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function VideoCard({ video, onPlay }) {
  // Self-hosted mp4 uses HTML5 video for thumbnail; YouTube uses static image.
  return (
    <button
      type="button"
      onClick={onPlay}
      data-testid={`video-${video.id}`}
      className="group text-left rounded-2xl overflow-hidden bg-white ring-1 ring-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-video bg-slate-900 overflow-hidden">
        {video.poster ? (
          <img src={video.poster} alt={video.title} loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
        ) : video.mp4Url ? (
          <video
            src={`${video.mp4Url}#t=0.5`}
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
          />
        ) : (
          <img
            src={video.thumb || video.poster}
            alt={video.title}
            loading="lazy"
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
          />
        )}
        {/* Play overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <Play size={22} className="text-brand-green ml-1" fill="currentColor" />
          </div>
        </div>
        {video.duration && (
          <span className="absolute bottom-3 right-3 bg-black/75 text-white text-xs font-semibold px-2 py-0.5 rounded">
            {video.duration}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-base font-semibold text-brand-blue mb-1.5 group-hover:text-brand-green transition-colors leading-snug">
          {video.title}
        </h3>
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <span className="font-medium text-gray-700">{video.name}</span>
          <span className="inline-flex items-center gap-0.5 text-xs"><MapPin size={11} /> {video.city}</span>
        </div>
      </div>
    </button>
  );
}
